export const blogPosts = [
  {
    slug: "designing-accessible-frontend-widgets",
    title: "Designing Accessible Frontend Widgets",
    description:
      "Notes on making interactive UI components keyboard-friendly and screen-reader aware.",
    subtitle:
      "Practical techniques for building inclusive, interactive UI elements without sacrificing polish.",
    publishedOn: "July 21, 2023",
    readTime: "6 min read",
    sections: [
      {
        heading: "Start with the right semantics",
        paragraphs: [
          "Accessibility work becomes much easier when the DOM already communicates intent. Before adding a single line of JavaScript, map the widget to the closest semantic element and fall back to ARIA roles only when the platform offers no native equivalent.",
          "For example, favour a native button element inside a disclosure widget instead of a clickable div. If you truly need a custom role, add the required states such as aria-expanded or aria-pressed so assistive technology can follow the interaction model.",
        ],
        list: {
          type: "unordered",
          items: [
            "Prefer native controls whenever possible.",
            "Provide explicit labels via aria-label or aria-labelledby when text is not visible.",
            "Keep the DOM order aligned with the visual order to avoid focus jumps.",
          ],
        },
      },
      {
        heading: "Manage focus intentionally",
        paragraphs: [
          "Keyboard users rely on predictable focus movement. Every interactive region should be reachable with Tab and Arrow keys in a way that mirrors platform conventions.",
          "Use roving tabindex for composite widgets such as carousels or listboxes, and trap focus only for modal surfaces where dismissal is obvious. When focus moves programmatically, call element.focus() after rendering so the browser performs the scroll and announcement work for you.",
        ],
      },
      {
        heading: "Announce dynamic updates",
        paragraphs: [
          "Screen readers cannot infer that a badge count changed or that a toast appeared unless we surface those changes. Leverage aria-live regions for small updates and focus management for larger context switches.",
          "A simple pattern is to add a visually hidden element with aria-live set to polite that you update with meaningful copy. Resist the urge to describe visual positioning; explain the outcome instead, such as ‘Added 1 task to In Progress’.",
        ],
      },
      {
        heading: "Test with real assistive technology",
        paragraphs: [
          "Automated tooling catches regressions, but nothing replaces a few minutes with VoiceOver, NVDA, or TalkBack. Run through the core interactions using only the keyboard and a screen reader at least once before shipping.",
          "Keep a short checklist in your pull requests so accessibility never becomes an afterthought. Iterating in small steps builds an inclusive muscle memory across the team.",
        ],
      },
    ],
  },
  {
    slug: "memoization-patterns-react",
    title: "Memoization Patterns in React",
    description:
      "A quick guide on when to reach for useMemo and useCallback while balancing readability.",
    subtitle:
      "Reduce unnecessary renders without turning your component tree into a maze of caches.",
    publishedOn: "August 14, 2023",
    readTime: "5 min read",
    sections: [
      {
        heading: "Understand what triggers renders",
        paragraphs: [
          "Before memoizing anything, profile the component to confirm that re-renders are the problem. Props changes, context updates, and state mutations all cascade down the tree, so start by inspecting why those updates happen.",
          "React DevTools highlights components re-rendering in response to interactions. Pair that with the flamegraph profiler to focus only on the paths that impact real user flows.",
        ],
      },
      {
        heading: "Choose the right primitive",
        paragraphs: [
          "useMemo caches derived values, while useCallback memoizes function references so children wrapped in React.memo stop re-rendering. Reach for useMemo when a calculation is expensive or when referential stability matters for dependencies.",
          "If you need to memoize complex objects, consider moving the state closer to where it is used instead. Lifting state solely to share data often leads to over-rendering that memoization only hides temporarily.",
        ],
        list: {
          type: "unordered",
          items: [
            "useMemo for derived data shared across renders.",
            "useCallback for callbacks passed to memoized children.",
            "React.memo for pure presentational components receiving stable props.",
          ],
        },
      },
      {
        heading: "Validate with measurements",
        paragraphs: [
          "After adding memoization, re-run your profiling scenario. You should see fewer renders and the same UI output. If performance does not improve, revert the change and investigate state ownership or batching instead.",
          "Remember that memoization adds cognitive overhead. Prefer simpler code unless you can link the optimization to a noticeable gain in the UX metrics you track.",
        ],
      },
      {
        heading: "Keep the API ergonomic",
        paragraphs: [
          "Memoization strategies often leak into component APIs through dependency arrays. Export helper hooks that encapsulate the memoization details so callers continue to pass declarative props.",
          "Document the scenarios in which a memoized hook or component should be used. Shared understanding prevents the team from scattering caches everywhere and makes future refactors less risky.",
        ],
      },
    ],
  },
  {
    slug: "optimization-techniques-react",
    title: "Optimization Techniques in React",
    description:
      "Explore practical strategies to keep React apps responsive as your component tree grows.",
    subtitle:
      "Profiling, prioritizing, and tuning React UI performance without premature complexity.",
    publishedOn: "September 5, 2023",
    readTime: "7 min read",
    sections: [
      {
        heading: "Measure before you optimize",
        paragraphs: [
          "Resist the urge to micro-optimize renders until you understand how the UI behaves in production scenarios. Start by capturing user timings with the Performance panel or web-vitals so you know which interactions feel sluggish.",
          "React DevTools Profiler highlights components responsible for the bulk of render time. Pair that with flame charts to identify whether the slowdown lives in React, the network, or a third-party dependency.",
        ],
      },
      {
        heading: "Tune render frequency",
        paragraphs: [
          "State colocated near the consumer re-renders fewer components. Before memoizing, audit state ownership and split large containers into focused hooks or contexts that only update their dependents.",
          "When heavy calculations are unavoidable, memoize the derived values with useMemo and wrap pure children in React.memo. Stabilize callback references with useCallback so memoized children can bail out confidently.",
        ],
        list: {
          type: "unordered",
          items: [
            "Move state closer to the component that consumes it.",
            "Consider splitting provider contexts by concern to reduce updates.",
            "Memoize expensive calculations that back render-critical props.",
          ],
        },
      },
      {
        heading: "Defer non-critical work",
        paragraphs: [
          "Leverage startTransition for updates that do not block user input, such as filtering large lists, so React prioritizes keeping interactions snappy.",
          "Lazy load routes and heavy components with React.lazy or dynamic imports. Combine that with Suspense boundaries to avoid stalling the entire screen while secondary UI loads.",
        ],
      },
      {
        heading: "Verify with runtime metrics",
        paragraphs: [
          "Ship feature flags or staged rollouts for significant performance changes and monitor core web vitals like FID and INP to validate the impact.",
          "Keep a short playbook that documents the optimization, the metric you targeted, and the outcome. Future contributors should understand why the complexity exists and when it can be removed.",
        ],
      },
    ],
  },
  {
    slug: "importance-of-lighthouse-scores",
    title: "Importance of Lighthouse Scores in Your App",
    description:
      "Understand what Lighthouse measures and how those scores influence user experience and business outcomes.",
    subtitle:
      "Translate performance audits into actionable improvements across performance, accessibility, and SEO.",
    publishedOn: "October 2, 2023",
    readTime: "6 min read",
    sections: [
      {
        heading: "What Lighthouse evaluates",
        paragraphs: [
          "Lighthouse runs a headless Chrome audit that scores performance, accessibility, best practices, and SEO. Each category bundles real user expectations such as first contentful paint, color contrast, and secure network usage.",
          "Rather than chasing a perfect 100, treat the report as a prioritized backlog. Focus on the audits marked as high impact first because they correlate most strongly with user-perceived quality.",
        ],
      },
      {
        heading: "Why the scores matter",
        paragraphs: [
          "Higher performance scores align with faster load times, which directly influence conversion rates and retention. Accessibility and best practices scores also reduce legal risk and support a broader audience.",
          "SEO audits surface metadata gaps, structured data issues, and mobile friendliness. Fixing them helps search engines index your app correctly, amplifying your reach without additional marketing spend.",
        ],
        quote:
          "Lighthouse distills thousands of micro-optimizations into a handful of actionable opportunities that move core business metrics.",
      },
      {
        heading: "Building a culture around audits",
        paragraphs: [
          "Automate Lighthouse runs in CI so regressions never slip through unnoticed. Set a budget for critical metrics like Largest Contentful Paint and fail the build when thresholds are exceeded.",
          "Share the report with designers and product managers. Framing the scores as shared goals encourages collaboration on performance-friendly UX patterns and content strategies.",
        ],
        list: {
          type: "ordered",
          items: [
            "Integrate Lighthouse CI with performance budgets.",
            "Track historical scores to visualize trends.",
            "Celebrate improvements to keep the team invested.",
          ],
        },
      },
      {
        heading: "Turning insights into action",
        paragraphs: [
          "Map each failing audit to an owner and timeline. Bundle quick wins like image compression and code splitting into the current sprint, and log larger efforts such as design system updates for roadmap planning.",
          "Repeat the audit after every release candidate. Continuous measurement keeps your app fast, accessible, and discoverable as new features land.",
        ],
      },
    ],
  },
];
